import type { HTMLAttributes } from 'react'
import {
  cn,
  daysUntil,
  formatDate,
  formatDateTime,
  formatStamp,
  isDateOnly,
  isOverdue,
  zoneTag,
  zoneTitle,
} from '@/shared/lib'

// Абсолютная дата: таймстампы `16.07.2026, 14:32` — в зоне устройства, день —
// `16.07.2026` (форма значения различается автоматически, у дня зоны нет вовсе).
// Для сроков (deadline) — подсветка цветом: просрочен → overdue, на подходе →
// due-soon; считается она по календарным суткам зоны организации, как на бэке.
// Полная дата словами — в title, там же названа зона; у дня вместо зоны сказано,
// что времени у него нет.
//
// `zone` дорисовывает видимый тег зоны — для ОДИНОЧНОГО момента (карточка,
// деталь, диалог подтверждения). В списках и таблицах зона подписывается один
// раз сверху (`ZoneNote` / `zoneColumnLabel`), иначе выходит частокол одинаковых
// тегов. У дня тег не появляется никогда, даже с `zone`: зоны у дня нет.
//
// Просрочка и «скоро» считаются ровно тем же правилом, что на бэке: у дня —
// сравнением дней в зоне организации, у момента — сравнением моментов. Иначе
// одна задача краснеет в списке и остаётся зелёной в карточке, где флаг
// приходит с сервера.

/** Порог «срок на подходе» в днях. */
export const DUE_SOON_DAYS = 3

/** Тон срока: просрочен / на подходе / обычный (null — раскрашивать нечем). */
export function deadlineTone(
  value: string | null | undefined,
): 'text-overdue' | 'text-due-soon' | null {
  if (!value) return null
  if (isOverdue(value)) return 'text-overdue'
  const days = daysUntil(value)
  return days != null && days <= DUE_SOON_DAYS ? 'text-due-soon' : null
}

interface DateStampProps extends Omit<HTMLAttributes<HTMLTimeElement>, 'children'> {
  value: string | null | undefined
  deadline?: boolean
  /** Видимый тег зоны у момента — для одиночного значения, не для строки списка. */
  zone?: boolean
}

export function DateStamp({
  value,
  deadline = false,
  zone = false,
  className,
  ...rest
}: DateStampProps) {
  if (!value) return <span className={cn('text-text-tertiary', className)}>—</span>

  const day = isDateOnly(value)
  const tone = deadline ? deadlineTone(value) : null
  return (
    <time
      dateTime={value}
      title={
        day
          ? `${formatDate(value)} — календарный день, без времени`
          : `${formatDateTime(value)} · ${zoneTitle()}`
      }
      className={cn(tone ?? 'text-text-secondary', className)}
      {...rest}
    >
      {formatStamp(value)}
      {zone && !day && <span className="ml-1 text-text-tertiary">{zoneTag()}</span>}
    </time>
  )
}
